import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { I18nProvider } from './context/I18nContext';
import { StoreFilterProvider } from './context/StoreFilterContext';
import { DataVersionProvider } from './context/DataVersionContext';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { ReportFiltersProvider } from './context/ReportFiltersContext';
import { DataCoverageProvider } from './context/DataCoverageContext';
import { SidebarProvider } from './context/SidebarContext';
import { ChartsVisibilityProvider } from './context/ChartsContext';
import ProtectedRoute from './components/ProtectedRoute';
import { Loader2 } from 'lucide-react';

const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Stores = lazy(() => import('./pages/Stores'));
const Sellers = lazy(() => import('./pages/Sellers'));
const Tickets = lazy(() => import('./pages/Tickets'));
const Reports = lazy(() => import('./pages/Reports'));
const Top10 = lazy(() => import('./pages/Top10'));
const Engagement = lazy(() => import('./pages/Engagement'));
const PodiumMapping = lazy(() => import('./pages/PodiumMapping'));
const Districts = lazy(() => import('./pages/Districts'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const Uploads = lazy(() => import('./pages/Uploads'));
const Analytics = lazy(() => import('./pages/Analytics'));
const AnalyticsHub = lazy(() => import('./pages/AnalyticsHub'));
const Analytics3D = lazy(() => import('./pages/Analytics3DPage'));
const AdminHub = lazy(() => import('./pages/AdminHub'));
const Commissions = lazy(() => import('./pages/Commissions'));
const AR317 = lazy(() => import('./pages/AR317'));
const Deliveries = lazy(() => import('./pages/Deliveries'));
const Inventory = lazy(() => import('./pages/Inventory'));
const InventoryReports = lazy(() => import('./pages/InventoryReports'));
const InventoryZeroSales = lazy(() => import('./pages/InventoryZeroSales'));
const InventoryAged = lazy(() => import('./pages/InventoryAged'));
const PaymentsOrphans = lazy(() => import('./pages/PaymentsOrphans'));
const ProfitMargin = lazy(() => import('./pages/ProfitMargin'));
const VisualDashboard = lazy(() => import('./pages/VisualDashboard'));
const VisualDashboardPresentation = lazy(() => import('./pages/VisualDashboardPresentation'));
const Welcome = lazy(() => import('./pages/Welcome'));
const Reconciliation = lazy(() => import('./pages/Reconciliation'));
const Alerts = lazy(() => import('./pages/Alerts'));
const Forecasting = lazy(() => import('./pages/Forecasting'));
const Comparisons = lazy(() => import('./pages/Comparisons'));
const MyPerformance = lazy(() => import('./pages/MyPerformance'));
const Users = lazy(() => import('./pages/Users'));
const Audit = lazy(() => import('./pages/Audit'));
const Catalog = lazy(() => import('./pages/Catalog'));
const Goals = lazy(() => import('./pages/Goals'));
const GoalsVsActual = lazy(() => import('./pages/GoalsVsActual'));
const ProtectionPlans = lazy(() => import('./pages/ProtectionPlans'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const ProductCatalog = lazy(() => import('./pages/ProductCatalog'));
const DeadStock = lazy(() => import('./pages/DeadStock'));
const ProductsHub = lazy(() => import('./pages/ProductsHub'));
const ReconciliationHub = lazy(() => import('./pages/ReconciliationHub'));
const PublicDashboard = lazy(() => import('./pages/PublicDashboard'));
const PublicSellerDetail = lazy(() => import('./pages/PublicSellerDetail'));
const PublicStoreDetail = lazy(() => import('./pages/PublicStoreDetail'));
const PublicPresentation = lazy(() => import('./pages/PublicPresentation'));
const PublicProductCatalog = lazy(() => import('./pages/PublicProductCatalog'));
const PublicShowroom = lazy(() => import('./pages/PublicShowroom'));
const PublicProductPresentation = lazy(() => import('./pages/PublicProductPresentation'));
const PublicChallengesBoard = lazy(() => import('./pages/PublicChallengesBoard'));
const PublicChallengesTV = lazy(() => import('./pages/PublicChallengesTV'));
const PublicTop10 = lazy(() => import('./pages/PublicTop10'));
const DashboardWall = lazy(() => import('./pages/DashboardWall'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const ChallengesPage = lazy(() => import('./pages/ChallengesPage'));
const MerchSets = lazy(() => import('./pages/MerchSets'));
const MerchSetsPhotos = lazy(() => import('./pages/MerchSetsPhotos'));
const Collections = lazy(() => import('./pages/Collections'));
const TagsAudit = lazy(() => import('./pages/TagsAudit'));
const DataQualityPage = lazy(() => import('./pages/DataQualityPage'));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
    <Loader2 className="w-8 h-8 text-[#0e2a73] animate-spin" />
  </div>
);

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <ThemeProvider>
          <AuthProvider>
            <SidebarProvider>
            <ChartsVisibilityProvider>
            <WorkspaceProvider>
              <StoreFilterProvider>
                <DataVersionProvider>
                  <ReportFiltersProvider>
                  <DataCoverageProvider>
                  <BrowserRouter>
                  <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      <Route path="/" element={<Login />} />
                      <Route path="/d/:token" element={<PublicDashboard />} />
                      <Route path="/d/:token/presentacion" element={<PublicPresentation />} />
                      <Route path="/d/:token/v/:sellerId" element={<PublicSellerDetail />} />
                      <Route path="/d/:token/t/:storeName" element={<PublicStoreDetail />} />
                      <Route path="/p/productos/:token" element={<PublicProductCatalog />} />
                      <Route path="/p/productos/:token/showroom" element={<PublicShowroom />} />
                      <Route path="/p/productos/:token/presenta/:handle" element={<PublicProductPresentation />} />
                      <Route path="/p/retos/:token/tv" element={<PublicChallengesTV />} />
                      <Route path="/p/retos/:token" element={<PublicChallengesBoard />} />
                      <Route path="/p/top10/:token" element={<PublicTop10 />} />
                      <Route path="/wall" element={<DashboardWall />} />
                      <Route path="/bienvenida" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
                      <Route path="/dashboard" element={<ProtectedRoute denyRoles="vendedor"><Dashboard /></ProtectedRoute>} />
                      <Route path="/cargas" element={<ProtectedRoute denyRoles="vendedor"><AdminHub /></ProtectedRoute>} />
                      <Route path="/analitica" element={<ProtectedRoute denyRoles="vendedor"><AnalyticsHub /></ProtectedRoute>} />
                      <Route path="/analitica/overview" element={<ProtectedRoute denyRoles="vendedor"><Analytics /></ProtectedRoute>} />
                      <Route path="/analitica/3d" element={<ProtectedRoute denyRoles="vendedor"><Analytics3D /></ProtectedRoute>} />
                      <Route path="/comisiones" element={<ProtectedRoute><Commissions /></ProtectedRoute>} />
                      <Route path="/ar317" element={<ProtectedRoute denyRoles="vendedor"><ReconciliationHub /></ProtectedRoute>} />
                      <Route path="/entregas" element={<ProtectedRoute denyRoles="vendedor"><ReconciliationHub /></ProtectedRoute>} />
                      <Route path="/inventario" element={<ProtectedRoute denyRoles="vendedor"><ProductsHub /></ProtectedRoute>} />
                      <Route path="/inventario/reportes" element={<ProtectedRoute denyRoles="vendedor"><InventoryReports /></ProtectedRoute>} />
                      <Route path="/inventario/sin-ventas" element={<ProtectedRoute denyRoles="vendedor"><ProductsHub /></ProtectedRoute>} />
                      <Route path="/inventario/antiguedad" element={<ProtectedRoute denyRoles="vendedor"><ProductsHub /></ProtectedRoute>} />
                      <Route path="/pagos-sin-orden" element={<ProtectedRoute denyRoles="vendedor"><ReconciliationHub /></ProtectedRoute>} />
                      <Route path="/reportes/margen" element={<ProtectedRoute denyRoles="vendedor"><ProductsHub /></ProtectedRoute>} />
                      <Route path="/reportes/garantias" element={<ProtectedRoute denyRoles="vendedor"><AnalyticsHub /></ProtectedRoute>} />
                      <Route path="/productos/:sku" element={<ProtectedRoute denyRoles="vendedor"><ProductDetail /></ProtectedRoute>} />
                      <Route path="/catalogo-productos" element={<ProtectedRoute denyRoles="vendedor"><ProductsHub /></ProtectedRoute>} />
                      <Route path="/catalogo/sets" element={<ProtectedRoute denyRoles="vendedor"><MerchSets /></ProtectedRoute>} />
                      <Route path="/catalogo/sets/fotos" element={<ProtectedRoute denyRoles="vendedor"><MerchSetsPhotos /></ProtectedRoute>} />
                      <Route path="/catalogo/colecciones" element={<ProtectedRoute denyRoles="vendedor"><Collections /></ProtectedRoute>} />
                      <Route path="/catalogo/colecciones/:slug" element={<ProtectedRoute denyRoles="vendedor"><Collections /></ProtectedRoute>} />
                      <Route path="/catalogo/tags" element={<ProtectedRoute requireRole="admin"><TagsAudit /></ProtectedRoute>} />
                      <Route path="/inventario/muerto" element={<ProtectedRoute denyRoles="vendedor"><ProductsHub /></ProtectedRoute>} />
                      <Route path="/graficos" element={<ProtectedRoute denyRoles="vendedor"><VisualDashboard /></ProtectedRoute>} />
                      <Route path="/graficos/presentacion" element={<ProtectedRoute denyRoles="vendedor"><VisualDashboardPresentation /></ProtectedRoute>} />
                      <Route path="/conciliacion" element={<ProtectedRoute denyRoles="vendedor"><ReconciliationHub /></ProtectedRoute>} />
                      <Route path="/alertas" element={<ProtectedRoute denyRoles="vendedor"><AnalyticsHub /></ProtectedRoute>} />
                      <Route path="/pronostico" element={<ProtectedRoute denyRoles="vendedor"><AnalyticsHub /></ProtectedRoute>} />
                      <Route path="/comparativos" element={<ProtectedRoute denyRoles="vendedor"><AnalyticsHub /></ProtectedRoute>} />
                      <Route path="/mi-desempeno" element={<ProtectedRoute><MyPerformance /></ProtectedRoute>} />
                      <Route path="/admin" element={<ProtectedRoute requireRole="admin"><AdminHub /></ProtectedRoute>} />
                      <Route path="/usuarios" element={<ProtectedRoute requireRole="admin"><AdminHub /></ProtectedRoute>} />
                      <Route path="/historial" element={<ProtectedRoute requireRole="admin"><AdminHub /></ProtectedRoute>} />
                      <Route path="/catalogo" element={<ProtectedRoute denyRoles="vendedor"><AdminHub /></ProtectedRoute>} />
                      <Route path="/metas" element={<ProtectedRoute denyRoles="vendedor"><AdminHub /></ProtectedRoute>} />
                      <Route path="/tiendas" element={<ProtectedRoute denyRoles="vendedor"><AnalyticsHub /></ProtectedRoute>} />
                      <Route path="/franquicias" element={<ProtectedRoute denyRoles="vendedor"><AnalyticsHub /></ProtectedRoute>} />
                      <Route path="/vendedores" element={<ProtectedRoute denyRoles="vendedor"><Sellers /></ProtectedRoute>} />
                      <Route path="/tickets" element={<ProtectedRoute denyRoles="vendedor"><ReconciliationHub /></ProtectedRoute>} />
                      <Route path="/reportes" element={<ProtectedRoute denyRoles="vendedor"><Reports /></ProtectedRoute>} />
                      <Route path="/reportes/top-10" element={<ProtectedRoute denyRoles="vendedor"><Top10 /></ProtectedRoute>} />
                      <Route path="/reportes/metas-vs-vendido" element={<ProtectedRoute denyRoles="vendedor"><GoalsVsActual /></ProtectedRoute>} />
                      <Route path="/engagement" element={<ProtectedRoute denyRoles="vendedor"><AnalyticsHub /></ProtectedRoute>} />
                      <Route path="/catalogos/podium-mapping" element={<ProtectedRoute denyRoles="vendedor"><PodiumMapping /></ProtectedRoute>} />
                      <Route path="/catalogos/distritos" element={<ProtectedRoute denyRoles="vendedor"><Districts /></ProtectedRoute>} />
                      <Route path="/admin/calidad-datos" element={<ProtectedRoute requireRole="admin"><DataQualityPage /></ProtectedRoute>} />
                      <Route path="/configuracion" element={<ProtectedRoute requireRole="admin"><AdminHub /></ProtectedRoute>} />
                      <Route path="/leaderboard" element={<ProtectedRoute denyRoles="vendedor"><Leaderboard /></ProtectedRoute>} />
                      <Route path="/retos" element={<ProtectedRoute><ChallengesPage /></ProtectedRoute>} />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
                  </DataCoverageProvider>
                  </ReportFiltersProvider>
                </DataVersionProvider>
              </StoreFilterProvider>
            </WorkspaceProvider>
            </ChartsVisibilityProvider>
            </SidebarProvider>
          </AuthProvider>
        </ThemeProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
